import { createClient } from "@supabase/supabase-js";
import type { Database } from "./database.types";
import { env } from "./env";

/** Where the Teams sign-in popup starts (`ROUTES.teamsSignInStart` in the web app). */
export const SIGN_IN_POPUP_PATH = "/auth/teams-start";
const POPUP_MARK = "amazing-care:sign-in-popup";

/**
 * Sessions live in localStorage, which every portal tab on this origin shares.
 * The Teams sign-in popup is a window on the same origin: in Teams on the web
 * it is an ordinary browser window. Its session belongs to the Teams tab, so
 * it keeps the session (and the PKCE verifier before it) in sessionStorage,
 * which is per window and survives the trip through Microsoft. Writing it to
 * localStorage would replace, and on hand-off erase, the session of a portal
 * tab already open in that browser.
 */
function sessionStore(): Storage {
  if (window.location.pathname === SIGN_IN_POPUP_PATH) {
    window.sessionStorage.setItem(POPUP_MARK, "1");
  }
  return window.sessionStorage.getItem(POPUP_MARK) ? window.sessionStorage : window.localStorage;
}

const storage = {
  getItem: (key: string) => sessionStore().getItem(key),
  setItem: (key: string, value: string) => sessionStore().setItem(key, value),
  removeItem: (key: string) => sessionStore().removeItem(key),
};

export const supabase = createClient<Database>(
  env.VITE_SUPABASE_URL || "http://placeholder.invalid",
  env.VITE_SUPABASE_PUBLISHABLE_KEY || "placeholder",
  {
    // PKCE returns a one-time code to /auth/callback instead of putting the
    // access token in the URL fragment, where it could end up in history.
    auth: {
      flowType: "pkce",
      ...(typeof window === "undefined" ? {} : { storage }),
    },
  }
);
